import { useEffect, useRef } from 'react';

import { OnboardDeviceLogsUploadStatus } from '../interfaces';
import { parseOnboardDeviceLogsSyncSnapshot } from '../utils/onboardDeviceLogsSync';
import { subscribeToWebSocket } from '../api/websocket/connection';

interface Params {
    enabled: boolean;
    requestId: string | null;
    onSnapshot: (snapshot: OnboardDeviceLogsUploadStatus) => void;
    /**
     * Fired on every open, including reconnects. The socket carries nothing
     * from while it was down, so this is where the caller reloads.
     */
    onConnected: () => void;
}

/**
 * Live sync progress, off the shared websocket.
 *
 * Each push carries the whole snapshot rather than a delta, so a re-delivered
 * one is a no-op and a missed one is corrected by the next push or by the
 * reload on reconnect.
 */
export const useOnboardDeviceLogsSyncSocket = ({ enabled, requestId, onSnapshot, onConnected }: Params) => {
    const onSnapshotRef = useRef(onSnapshot);
    onSnapshotRef.current = onSnapshot;

    const onConnectedRef = useRef(onConnected);
    onConnectedRef.current = onConnected;

    const requestIdRef = useRef(requestId);
    requestIdRef.current = requestId;

    useEffect(() => {
        if (!enabled) {
            return;
        }

        return subscribeToWebSocket({
            onMessage: (data) => {
                const snapshot = parseOnboardDeviceLogsSyncSnapshot(data);

                // The socket carries every device's syncs, so drop what belongs
                // to somebody else's dialog.
                if (!snapshot || snapshot.requestId !== requestIdRef.current) {
                    return;
                }

                onSnapshotRef.current(snapshot);
            },
            onConnected: () => onConnectedRef.current(),
        });
    }, [enabled]);
};
